import React from "react";
import { Trans, useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { useDispatch } from "src/store";
import { Tooltip } from "src/ui/shared/Tooltip";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import { updateGroupMembers } from "@dashboard/devices/store";
import { Group, GroupMember } from "@dashboard/devices/types";
import { RoleLabel } from "@dashboard/shared/components/RoleLabel";
import { getAssignableRoleOptions, getRoleLabel, rolesOptions } from "@dashboard/shared/roles";
import { DialogContentText } from "@mui/material";
import { memberSubject, PermissionService } from "@services/permissionService/permissionService";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { showSuccessToast } from "@shared/toasts/Toasts";

import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "../../api/helpers";

function validateRole(role: unknown) {
    if (role !== "Owner" && role !== "Manager" && role !== "User") {
        throw new Error(i18n.t("pages:workspacePage.toast.invalidRole"));
    }
    return role;
}

type Props = {
    group: Group;
    member: GroupMember;
};

export const ChangeGroupRoleField = (props: Props) => {
    const [pendingRole, setPendingRole] = React.useState<GroupMember["role"] | null>(null);

    const { t } = useTranslation(["common", "pages", "modals", "sidepanels"]);
    const dispatch = useDispatch();

    const isExpired = useCurrentSubscriptionExpired();
    const { data: workspace } = useCurrentWorkspace();
    const { groupAbility } = PermissionService();
    const ability = groupAbility(props.group, workspace);
    const mayManageMember = ability.can("manage", "Member");
    const canAssignOwner = ability.can("update", memberSubject("Owner"));
    const value = props.group.members.find((member) => member.email === props.member.email)?.role;

    const mayEditThisMember = ability.can("update", memberSubject(value));
    const options = getAssignableRoleOptions(canAssignOwner);

    const getTooltip = () => {
        if (isExpired) {
            return t("pages:workspacePage.tooltip.subscriptionExpired");
        }
        if (!mayEditThisMember) {
            return t("sidepanels:groupPanel.tooltip.cannotEditOwnerRole");
        }

        return null;
    };

    const onChangeRole: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "string" || selectedOption.value === value) {
            return;
        }
        setPendingRole(validateRole(selectedOption.value));
    };

    const onConfirmRoleChange = async () => {
        if (!pendingRole) {
            return;
        }

        await dispatch(
            updateGroupMembers({ id: props.group.id, changedMembers: { [props.member.email]: pendingRole } }),
        ).unwrap();
        showSuccessToast(t("pages:workspacePage.toast.roleChangedSuccess"));
    };

    const tooltip = getTooltip();

    // A member who cannot manage members can never edit roles, so show the role as plain text.
    if (!mayManageMember) {
        return <RoleLabel role={value} />;
    }

    return (
        <React.Fragment>
            <Tooltip title={tooltip} disableChildrenWrapper>
                <SelectField
                    value={rolesOptions.find((option) => option.value === value)}
                    options={options}
                    onChange={onChangeRole}
                    disabled={!!tooltip}
                    fullWidth
                    disableSorting
                />
            </Tooltip>

            <ConfirmDialog
                title={t("modals:changeMemberRole.title")}
                primaryActionText={t("common:button.change")}
                open={!!pendingRole}
                onConfirm={onConfirmRoleChange}
                onClose={() => setPendingRole(null)}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="changeMemberRole.descriptionGroup"
                        values={{
                            email: props.member.email,
                            name: props.group.name,
                            oldRole: getRoleLabel(value),
                            newRole: getRoleLabel(pendingRole),
                        }}
                        components={{ bold: <strong /> }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </React.Fragment>
    );
};
